import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column, SortingRule } from 'react-table';
import SimpleReactTable from '../../../../../../generic/SimpleReactTable';
import TableHeader from '../../../../../../generic/TableHeader';
import type { TeamGroupTeamData } from '../../../../../../../../shared/types/routes/teamGroups';
import SettingsPageTableActions from '../../../../../../shared/settings/SettingsPageTableActions';
import AddTeamModal from './AddTeamModal';
import { userHasPermissionForTeamProjects } from '../../../../../../../helpers/permissions';
import { Action } from '../../../../../../../../typings/permissions';
import TeamGroupTeamsTableRemove from './TeamGroupTeamsTableRemove';

const defaultSortMode = [
  {
    id: 'name',
    desc: false,
  },
];

const useTeamGroupsColumns = (
  sortMode: SortingRule,
  teamGroupId: number,
): Array<Column<TeamGroupTeamData>> => useMemo(() => [
  {
    id: 'name',
    Header: <TableHeader title="Team Name" sort={{ id: 'name', currentMode: sortMode }} />,
    accessor: 'name',
    resizable: false,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'projects',
    Header: <TableHeader title="Projects" sort={{ id: 'projects', currentMode: sortMode }} />,
    accessor: 'numProjects',
    width: 250,
    resizable: false,
  },
  {
    id: 'actions',
    Header: <TableHeader title="Remove" />,
    accessor: () => null,
    Cell: ({ original }) => (
      <TeamGroupTeamsTableRemove
        teamId={original.id}
        teamGroupId={teamGroupId}
        />
    ),
    width: 70,
    resizable: false,
    sortable: false,
  },
], [sortMode, teamGroupId]);

/**
 * Filters the team group teams based on the search term.
 * @param {TeamGroupTeamData[]} teamGroupTeams - The list of team group teams to filter
 * @param {string} search - The search term
 * @returns {TeamGroupTeamData[]} The filtered list of team group teams
 */
const useFilteredTeamGroups = (teamGroupTeams: TeamGroupTeamData[], search: string) => useMemo(() => {
  if (!teamGroupTeams) {
    return [];
  }
  if (!search) {
    return teamGroupTeams;
  }
  const lowerSearch = search.toLowerCase();
  return teamGroupTeams.filter((teamGroupTeam) => teamGroupTeam.name.toLowerCase().includes(lowerSearch));
}, [teamGroupTeams, search]);

type Props = {
  //  This is managed in the parent component (because it's used for both the team groups and teams tables).
  isLoading: boolean;
  teamGroupId: number;
  teamGroupName: string;
  teamGroupTeams: TeamGroupTeamData[];
};

/**
 * Displays the list of team groups
 */
const TeamGroupTeamsTable: VoidFunctionComponent<Props> = ({
  isLoading,
  teamGroupId,
  teamGroupName,
  teamGroupTeams,
}) => {
  const canAddTeam = userHasPermissionForTeamProjects(Action.Add, teamGroupId);
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode[0] as SortingRule);
  const filteredTeamGroupTeams = useFilteredTeamGroups(teamGroupTeams, search);
  const columns = useTeamGroupsColumns(sortMode, teamGroupId);

  return (
    <>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-team-to-team-group-button"
          size="large"
          type="secondary"
          onClick={() => setIsAddingTeam(true)}
          isDisabled={!canAddTeam}
          showTooltip={!canAddTeam}
          tooltip="You are not permitted to add teams to this team group."
        >
          Add team
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          label="Search team group teams"
          value={search}
          onChange={setSearch}
          placeholder="Search team group teams"
          size="medium"
        />
      </SettingsPageTableActions>
      <SimpleReactTable
        data={filteredTeamGroupTeams}
        columns={columns}
        defaultSortMode={defaultSortMode}
        onSortedChanged={setSortMode}
        showPagination={filteredTeamGroupTeams.length > 20}
        cacheId="TeamGroupTeams"
        isLoading={isLoading}
      />
      <AddTeamModal
        isOpen={isAddingTeam}
        onHide={() => setIsAddingTeam(false)}
        teamGroupName={teamGroupName}
        teamGroupId={teamGroupId}
        teamGroupTeams={teamGroupTeams}
      />
    </>
  );
};

export default TeamGroupTeamsTable;
